<template>
  <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
    <b-form-group label="Title" label-for="title" label-class="form-label">
      <b-form-input
        id="title"
        placeholder="Enter research title."
        v-model.trim="research.title"
        required
        :state="!$v.research.title.required && $v.research.title.$dirty ? false : null"
      />
      <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
    </b-form-group>

    <b-form-group label="Description" label-for="description" label-class="form-label">
      <editor
        :init="{
          height: 400,
          menubar: 'edit view insert format tools table help',
          plugins: [
            'advlist autolink lists link image charmap print preview anchor',
            'searchreplace visualblocks code fullscreen',
            'insertdatetime media table paste code help wordcount',
          ],
          toolbar:
            'undo redo | formatselect | bold italic backcolor | \
           alignleft aligncenter alignright alignjustify | \
           bullist numlist outdent indent | removeformat | help',
        }"
        v-model="research.description"
        placeholder="Enter research description."
      />
      <b-form-invalid-feedback :state="!$v.research.description.required && $v.research.description.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>

    <b-form-group label="Status" label-for="status" label-class="form-label" v-if="$route.params.id">
      <v-select
        id="status"
        class="form-control v-select-custom"
        label="text"
        v-model="research.is_active"
        :reduce="(option) => option.value"
        placeholder="Select status"
        :options="activeOptions"
        :clearable="false"
      >
        <template #search="{ attributes, events }">
          <input class="vs__search" :required="research.is_active === null" v-bind="attributes" v-on="events" />
        </template>
      </v-select>
    </b-form-group>

    <div class="row">
      <b-form-group label="Type" label-for="type" label-class="form-label" class="col-md-6">
        <v-select
          id="type"
          class="form-control v-select-custom"
          label="text"
          v-model="research.type"
          :reduce="(option) => option.value"
          placeholder="Select type"
          :options="typeOptions"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
      </b-form-group>

      <b-form-group label="Schedule" label-for="schedule" label-class="form-label" class="col-md-6">
        <v-select
          id="schedule"
          class="form-control v-select-custom"
          label="text"
          v-model="research.schedule"
          :reduce="(option) => option.value"
          placeholder="Select schedule"
          :options="scheduleOptions"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
      </b-form-group>
    </div>

    <b-form-group label="Application Link" label-for="applicationLink" label-class="form-label">
      <b-form-input
        id="applicationLink"
        placeholder="Enter application/info link."
        v-model.trim="research.application_link"
        :state="!$v.research.application_link.url && $v.research.application_link.$dirty ? false : null"
      />
      <b-form-invalid-feedback
        >Not a valid link. Please enter a valid link e.g. https://www.example.com.</b-form-invalid-feedback
      >
    </b-form-group>

    <!-- Research-specific details -->
    <div class="row">
      <b-form-group label="Faculty / PI / Lab" label-for="faculty_name" label-class="form-label" class="col-md-6">
        <b-form-input
          id="faculty_name"
          placeholder="Principal investigator or lab name"
          v-model.trim="research.faculty_name"
        />
      </b-form-group>

      <b-form-group label="Department" label-for="department" label-class="form-label" class="col-md-6">
        <b-form-input id="department" placeholder="Enter department" v-model.trim="research.department" />
      </b-form-group>
    </div>

    <b-form-group label="Location" label-for="location" label-class="form-label">
      <b-form-input
        id="location"
        placeholder="e.g. On-campus (Building 4, Lab 2) or Remote"
        v-model.trim="research.location"
      />
    </b-form-group>

    <div class="row">
      <b-form-group label="Time Commitment" label-for="time_commitment" label-class="form-label" class="col-md-6">
        <b-form-input
          id="time_commitment"
          placeholder="e.g. 10 hrs/week for one semester"
          v-model.trim="research.time_commitment"
        />
      </b-form-group>

      <b-form-group label="Application Deadline" label-for="deadline" label-class="form-label" class="col-md-6">
        <b-form-input id="deadline" type="date" v-model="research.deadline" />
      </b-form-group>
    </div>

    <b-form-group label="Eligibility" label-for="eligibility" label-class="form-label">
      <b-form-textarea
        id="eligibility"
        placeholder="Who can apply, prerequisites, required background, etc."
        v-model.trim="research.eligibility"
        rows="3"
      />
    </b-form-group>

    <div class="row">
      <b-form-group class="col-md-6">
        <b-form-checkbox v-model="research.is_remote">This is a remote opportunity</b-form-checkbox>
      </b-form-group>
      <b-form-group class="col-md-6">
        <b-form-checkbox v-model="research.is_paid">This is a paid opportunity</b-form-checkbox>
      </b-form-group>
    </div>

    <b-form-group label="Stipend" label-for="stipend" label-class="form-label" v-if="research.is_paid">
      <currency-input
        v-model="research.stipend"
        class="form-control"
        placeholder="Enter stipend amount (optional)"
        :allow-negative="false"
      />
    </b-form-group>

    <!-- Address Fields -->
    <b-form-group label="Address" label-for="address" label-class="form-label">
      <b-form-input id="address" placeholder="Enter address" v-model="research.address" />
    </b-form-group>

    <div class="row">
      <b-form-group label="Zipcode" label-for="zipcode" label-class="form-label" class="col-md-6">
        <b-form-input id="zipcode" placeholder="Enter zipcode" v-model="research.zipcode" />
      </b-form-group>

      <b-form-group label="City" label-for="city" label-class="form-label" class="col-md-6">
        <b-form-input id="city" placeholder="Enter city" v-model="research.city" />
      </b-form-group>
    </div>

    <div class="row">
      <b-form-group label="State" label-for="state" label-class="form-label" class="col-md-6">
        <v-select
          id="state"
          class="form-control v-select-custom"
          label="text"
          v-model="research.state"
          :reduce="(state) => state.value"
          placeholder="Select State"
          :options="stateOptions"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
      </b-form-group>

      <b-form-group label="Country" label-for="country" label-class="form-label" class="col-md-6">
        <v-select
          id="country"
          class="form-control v-select-custom"
          label="text"
          v-model="research.country"
          :reduce="(country) => country.value"
          placeholder="Select country"
          :options="countryOptions"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
      </b-form-group>
    </div>

    <!-- Pay/Salary details -->
    <div class="row">
      <b-form-group label="Minimum Pay" label-for="minimum_pay" label-class="form-label" class="col-md-6">
        <currency-input
          v-model="research.minimum_pay"
          class="form-control"
          placeholder="Enter minimum pay (optional)"
          :allow-negative="false"
        />
      </b-form-group>

      <b-form-group label="Maximum Pay" label-for="maximum_pay" label-class="form-label" class="col-md-6">
        <currency-input
          v-model="research.maximum_pay"
          class="form-control"
          placeholder="Enter maximum pay (optional)"
          :allow-negative="false"
          :value-range="{ ...(research.minimum_pay && { min: research.minimum_pay }) }"
        />
      </b-form-group>
    </div>

    <div class="row">
      <b-form-group label="Pay Rate" label-for="pay_rate" label-class="form-label" class="col-md-6">
        <v-select
          id="pay_rate"
          class="form-control v-select-custom"
          label="text"
          v-model="research.pay_rate"
          :reduce="(rate) => rate.value"
          placeholder="Select pay rate"
          :options="payRateOptions"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
      </b-form-group>

      <b-form-group label="Number of Positions" label-for="no_of_hires" label-class="form-label" class="col-md-6">
        <currency-input
          v-model="research.no_of_hires"
          class="form-control"
          placeholder="Enter number of positions (optional)"
          :currency="null"
          :precision="0"
          :allow-negative="false"
        />
      </b-form-group>
    </div>
  </b-form>
</template>

<script>
import { countries } from 'countries-list';
import { map, pick } from 'lodash';
import Editor from '@tinymce/tinymce-vue';
import { required, url } from 'vuelidate/lib/validators';
import { JOB_SCHEDULE_OPTIONS, JOB_TYPE_OPTIONS } from '../../../common/constants';
var UsaStates = require('usa-states').UsaStates;

export default {
  name: 'ResearchBasicInfoForm',
  components: { Editor },

  props: {
    researchData: { type: Object, required: true },
  },

  data() {
    return {
      research: {
        title: '',
        description: '',
        is_active: true,
        type: null,
        schedule: null,
        application_link: '',
        faculty_name: '',
        department: '',
        location: '',
        is_remote: false,
        is_paid: false,
        stipend: null,
        time_commitment: '',
        eligibility: '',
        deadline: null,
        minimum_pay: null,
        maximum_pay: null,
        pay_rate: null,
        no_of_hires: null,
        address: '',
        city: '',
        state: '',
        zipcode: '',
        country: 'United States',
      },
      firstLoad: true,
      countryOptions: [],
      stateOptions: [],
      typeOptions: JOB_TYPE_OPTIONS,
      scheduleOptions: JOB_SCHEDULE_OPTIONS,
      payRateOptions: [
        { value: 'hourly', text: 'Hourly' },
        { value: 'weekly', text: 'Weekly' },
        { value: 'monthly', text: 'Monthly' },
        { value: 'annually', text: 'Annually' },
      ],
      activeOptions: [
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive' },
      ],
    };
  },

  validations() {
    return {
      research: {
        title: { required },
        description: { required },
        application_link: { url },
      },
    };
  },

  watch: {
    researchData: {
      handler() {
        if (this.firstLoad) {
          this.research = pick(this.researchData, [
            'title',
            'description',
            'is_active',
            'type',
            'schedule',
            'application_link',
            'faculty_name',
            'department',
            'location',
            'is_remote',
            'is_paid',
            'stipend',
            'time_commitment',
            'eligibility',
            'deadline',
            'minimum_pay',
            'maximum_pay',
            'pay_rate',
            'no_of_hires',
            'address',
            'city',
            'state',
            'zipcode',
            'country',
          ]);
          this.firstLoad = false;
        }
      },
      deep: true,
    },
  },

  methods: {
    validateFormData() {
      this.$v.$touch();
      return {
        isValid: !this.$v.$invalid,
        formData: { ...this.research },
      };
    },

    fillInOptions() {
      var usStates = new UsaStates();
      this.stateOptions = this.stateOptions.concat(
        map(usStates.states, (state) => ({ value: state.name, text: state.name }))
      );
      this.countryOptions = this.countryOptions.concat(
        map(countries, (country) => ({ value: country.name, text: country.name }))
      );
    },
  },

  async mounted() {
    this.fillInOptions();
  },
};
</script>
